/**
 * Layout constants shared across AppLayout components.
 */

export const SIDEBAR_WIDTH = 280;
export const SIDEBAR_COLLAPSED_WIDTH = 80;
export const HEADER_HEIGHT = 64;
export const FOOTER_HEIGHT = 48;

/** TailAdmin-inspired color palette. */
export const COLORS = {
  sidebarBg: '#1C2434',
  sidebarBorder: '#313D4F',
  sidebarHoverBg: '#313D4F',
  navText: '#C8CFD8',
  navTextHover: '#FFFFFF',
  navTextActive: '#FFFFFF',
  sectionHeader: '#DEE4ED',
  accentPurple: '#6B3FA0',
  headerBg: '#FFFFFF',
  headerBorder: '#E2E8F0',
  contentBg: '#F8FAFC',
  chipInactive: '#EDF2F7',
  chipInactiveText: '#64748B',
};

/** Platform functionality definitions. */
export interface FunctionalityDef {
  id: string;
  label: string;
  description: string;
  defaultRoute: string;
}

export const FUNCTIONALITIES: FunctionalityDef[] = [
  {
    id: 'pricing',
    label: 'Products',
    description: 'Insurance Product Pricing',
    defaultRoute: '/pricing',
  },
  {
    id: 'policy_data',
    label: 'Policy Data',
    description: 'Life Policy Member Data & Cohorts',
    defaultRoute: '/policy-data',
  },
  {
    id: 'reinsurance',
    label: 'Reinsurance',
    description: 'Reinsurance Management',
    defaultRoute: '/reinsurance-module',
  },
  {
    id: 'budgeting',
    label: 'Budgeting',
    description: 'Insurance Budgeting & Forecasting',
    defaultRoute: '/budgeting',
  },
  {
    id: 'actuarial_model',
    label: 'Actuarial Model',
    description: 'IFRS 17 Actuarial Modelling',
    defaultRoute: '/actuarial-model',
  },
  {
    id: 'audit_workbook',
    label: 'Audit Workbook',
    description: 'IFRS 17 PAA Online Workbook',
    defaultRoute: '/dashboard',
  },
  {
    id: 'financial_condition_report',
    label: 'Financial Condition Report',
    description: 'Statutory FCR & Solvency Reporting',
    defaultRoute: '/financial-condition-report',
  },
];

export const FUNCTIONALITY_MAP = Object.fromEntries(
  FUNCTIONALITIES.map((f) => [f.id, f]),
);

// ─── Functionality-specific sidebar navigation ─────────────────────

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** If set, only users with one of these roles see this item. */
  roles?: string[];
  /** If true, item is only shown when IFRS 4 BS is enabled for the period. */
  ifrs4Only?: boolean;
}

export interface NavSection {
  label: string;
  collapsible?: boolean;
  items: NavItem[];
}

/**
 * Maps each functionality to its sidebar nav sections.
 * Generated from the unified route manifest — see routes/navBuilder.ts.
 * Role filtering is applied at render time — items without `roles` are visible to all.
 */
import { buildAllNavs } from '../../routes/navBuilder';

export const FUNCTIONALITY_NAV: Record<string, NavSection[]> = buildAllNavs();
